/**
 * catalog-picker — ONE picker for everything a journey offers.
 *
 * Before this, the Journey Designer had five ways to pick something bookable:
 * a two-pane grouped picker on Queue · Services, a flat checkbox list on
 * Appointment · Services (whose order was click order, shown as a number), a
 * second flat list in the page inspector that could disagree with Setup, three
 * columns of toggles on One Stop, and a plain Dropdown for staff and resources.
 * Only the queue one understood a DEPARTMENT as a thing you can select, and it
 * was the only one that kept that selection live as the catalogue grew.
 *
 * This component is that picker, generalised: source pane on the left grouped
 * by department with an Add-all per group and a search across everything, and
 * an ordered selection pane on the right. It picks services, staff, resources,
 * ticket types, queues and events — the caller supplies items and decides what
 * the meta line says.
 *
 * Selection shape is `{ type: "group" | "item", id }[]`, the contract the queue
 * picker already used. A "group" entry is a LIVE RULE: items added to that
 * department later are offered automatically, and `resolveSelection` is what
 * every consumer (and the Publish summary) uses to see what it resolves to
 * right now.
 */
import { useMemo, useState } from "react";

export interface PickerItem {
  id: string;
  name: string;
  /** Department id. Items whose group is unknown fall under "Ungrouped". */
  group?: string | null;
  /** Shown under the name, joined with " · ". Nulls are dropped. */
  meta?: (string | null | undefined)[];
  /** Extra text search matches on — description, keywords, aliases. */
  keywords?: string;
  /** False for an item that can still be RESOLVED and displayed but must not be
   *  offered as a new pick — a legacy entry on its way out (2026-09-02). The
   *  items array is both the offer list and the resolution set, so filtering
   *  such an entry out of `items` made existing selections unresolvable and a
   *  queue read as having no services at all. It is hidden from the source pane
   *  and honoured everywhere else. */
  offerable?: boolean;
}
export interface PickerGroup { id: string; name: string; hue?: string | null }
export type PickerSel = { type: "group" | "item"; id: string };

const FONT = "'Poppins', system-ui, sans-serif";
const MONO = { fontFamily: "'Roboto Mono', ui-monospace, monospace" } as const;
const SEC = { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" as const, color: "#697180" };
const GHOST = {
  height: 28, padding: "0 11px", borderRadius: 8, border: "1px solid #D1D4DC",
  background: "#FFFFFF", color: "#333333", fontSize: 11.5, fontWeight: 700,
  cursor: "pointer", fontFamily: FONT,
} as const;

/** What a selection resolves to right now, in selection order, de-duplicated.
 *  Live group rules expand here and nowhere else, so every reader of a
 *  selection — the pages, the Publish summary, the write-back — agrees. */
export function resolveSelection(sel: PickerSel[], items: PickerItem[]): PickerItem[] {
  const out: PickerItem[] = [];
  const seen = new Set<string>();
  const push = (it: PickerItem | undefined) => { if (it && !seen.has(it.id)) { seen.add(it.id); out.push(it); } };
  (sel ?? []).forEach((s) => {
    if (s.type === "group") items.filter((i) => (i.group ?? "") === s.id).forEach(push);
    else push(items.find((i) => i.id === s.id));
  });
  return out;
}

/** One line naming what the selection currently covers, for the Publish
 *  summary — the operator ruling was that a live rule must say what it resolves
 *  to today, not just that it is live. */
export function describeSelection(sel: PickerSel[], items: PickerItem[], groups: PickerGroup[], noun = "service") {
  const resolved = resolveSelection(sel, items);
  const rules = (sel ?? []).filter((s) => s.type === "group");
  const named = (sel ?? []).filter((s) => s.type === "item").length;
  if (!resolved.length) return `No ${noun}s yet`;
  const parts = [`${resolved.length} ${noun}${resolved.length === 1 ? "" : "s"}`];
  if (rules.length) {
    const names = rules.map((r) => groups.find((g) => g.id === r.id)?.name ?? "a department");
    parts.push(`${named ? `${named} picked individually, the rest from ` : "all of "}${names.join(", ")} — live, so new ${noun}s there are offered automatically`);
  }
  return parts.join(" · ");
}

export interface CatalogPickerProps {
  items: PickerItem[];
  groups?: PickerGroup[];
  value: PickerSel[];
  onChange: (next: PickerSel[]) => void;
  /** Left-pane heading. */
  sourceLabel?: string;
  /** Right-pane heading; the resolved count is appended. */
  selectedLabel?: string;
  /** Singular noun used in counts and empty states. */
  noun?: string;
  /** Off for One Stop, whose three lists are each one flat kind by design. */
  grouped?: boolean;
  /** Whole-department rules. Off for kinds that have no meaningful department. */
  allowGroups?: boolean;
  /** Selection order is meaningful \u2014 show drag handles and arrows. */
  ordered?: boolean;
  /** Pick exactly one (the per-page sample slot). */
  single?: boolean;
  searchPlaceholder?: string;
  emptySource?: string;
  emptySelection?: string;
  /** Compact single-column mode for narrow panels (the page inspector). */
  dense?: boolean;
}

export function CatalogPicker({
  items, groups = [], value, onChange,
  sourceLabel = "Your catalogue", selectedLabel = "Offered here", noun = "service",
  grouped = true, allowGroups = true, ordered = true, single = false,
  searchPlaceholder = "Search…", emptySource, emptySelection, dense = false,
}: CatalogPickerProps) {
  const [q, setQ] = useState("");
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const has = (type: "group" | "item", id: string) => (value ?? []).some((x) => x.type === type && x.id === id);
  const add = (type: "group" | "item", id: string) => {
    if (has(type, id)) return;
    onChange(single ? [{ type, id }] : [...(value ?? []), { type, id }]);
  };
  const removeAt = (i: number) => onChange((value ?? []).filter((_, x) => x !== i));
  const move = (i: number, d: number) => {
    const cur = [...(value ?? [])];
    const j = i + d;
    if (j < 0 || j >= cur.length) return;
    const [it] = cur.splice(i, 1);
    cur.splice(j, 0, it!);
    onChange(cur);
  };
  const dropOn = (i: number) => {
    if (drag === null || drag === i) return;
    const cur = [...(value ?? [])];
    const [it] = cur.splice(drag, 1);
    cur.splice(i, 0, it!);
    onChange(cur);
  };

  /* Search matches name, department name and keywords \u2014 the three the operator
     asked for. A match outside the open department still shows, with its
     department named beneath it, so a search is a way THROUGH the grouping
     rather than a second mode that ignores it. */
  const needle = q.trim().toLowerCase();
  const groupName = (id?: string | null) => groups.find((g) => g.id === id)?.name ?? "";
  /* The browse list excludes items that exist only to resolve an older
     selection — they are still in `items`, so the selection pane names them
     properly instead of calling them missing. */
  const offerable = useMemo(() => items.filter((i) => i.offerable !== false), [items]);
  const matches = useMemo(() => {
    if (!needle) return offerable;
    return offerable.filter((i) => (
      i.name.toLowerCase().includes(needle)
      || groupName(i.group).toLowerCase().includes(needle)
      || (i.keywords ?? "").toLowerCase().includes(needle)
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle, offerable, groups]);

  const resolved = resolveSelection(value ?? [], items);
  const coveredByRule = (it: PickerItem) => (value ?? []).some((s) => s.type === "group" && s.id === (it.group ?? ""));

  const itemRow = (it: PickerItem, showGroup: boolean) => {
    const on = has("item", it.id);
    const viaRule = coveredByRule(it);
    const meta = [...(it.meta ?? []).filter(Boolean), showGroup && it.group ? groupName(it.group) : null].filter(Boolean).join(" · ");
    return (
      <button
        key={it.id}
        onClick={() => (on ? onChange((value ?? []).filter((x) => !(x.type === "item" && x.id === it.id))) : add("item", it.id))}
        disabled={viaRule && !on}
        title={viaRule && !on ? `Already offered — its whole department is selected` : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 8, textAlign: "left", width: "100%",
          padding: "7px 9px", borderRadius: 8, fontFamily: FONT,
          border: `1px solid ${on ? "#5142C7" : "#EEF0F3"}`,
          background: on ? "#F6F2FE" : "#FFFFFF",
          cursor: viaRule && !on ? "default" : "pointer",
          opacity: viaRule && !on ? 0.55 : 1,
        }}>
        <span style={{ width: 14, height: 14, flex: "none", borderRadius: single ? 999 : 4, border: `1px solid ${on || viaRule ? "#5142C7" : "#D1D4DC"}`, background: on || viaRule ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", fontSize: 9, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{on || viaRule ? "✓" : ""}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#333333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
          {meta ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{meta}</span> : null}
        </span>
      </button>
    );
  };

  const visibleGroups = grouped
    ? groups.filter((g) => matches.some((i) => (i.group ?? "") === g.id))
    : [];
  const ungrouped = grouped
    ? matches.filter((i) => !i.group || !groups.some((g) => g.id === i.group))
    : matches;

  const source = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ ...SEC, flex: 1, minWidth: 0 }}>{sourceLabel}</span>
        <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "none", ...MONO }}>{resolved.length}/{offerable.length}</span>
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={searchPlaceholder}
        style={{ height: 32, padding: "0 10px", borderRadius: 8, border: "1px solid #D1D4DC", fontSize: 12, fontFamily: FONT, outline: "none", minWidth: 0 }} />
      {!offerable.length ? (
        <span style={{ fontSize: 12, color: "#B5BAC6" }}>{emptySource ?? `No ${noun}s available yet.`}</span>
      ) : !matches.length ? (
        <span style={{ fontSize: 12, color: "#B5BAC6" }}>Nothing matches “{q.trim()}”.</span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: dense ? 260 : 420, overflowY: "auto", paddingRight: 2 }}>
          {visibleGroups.map((g) => {
            const inG = matches.filter((i) => (i.group ?? "") === g.id);
            const ruleOn = has("group", g.id);
            const shut = collapsed[g.id] && !needle;
            return (
              <div key={g.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "#B5BAC6", fontSize: 10, flex: "none", width: 12 }}>{shut ? "▶" : "▼"}</button>
                  {g.hue ? <span style={{ width: 9, height: 9, borderRadius: 999, background: g.hue, flex: "none" }} /> : null}
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "#333333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</span>
                  <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "none" }}>{inG.length}</span>
                  {allowGroups && !single ? (
                    <button disabled={ruleOn} onClick={() => add("group", g.id)} style={{ ...GHOST, height: 24, padding: "0 9px", fontSize: 10.5, opacity: ruleOn ? 0.5 : 1, cursor: ruleOn ? "default" : "pointer", flex: "none" }}>{ruleOn ? "Added" : "Add all"}</button>
                  ) : null}
                </div>
                {!shut ? <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingLeft: 20 }}>{inG.map((i) => itemRow(i, false))}</div> : null}
              </div>
            );
          })}
          {ungrouped.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {grouped ? <span style={{ fontSize: 12.5, fontWeight: 700, color: "#333333" }}>Ungrouped</span> : null}
              <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingLeft: grouped ? 20 : 0 }}>{ungrouped.map((i) => itemRow(i, !!needle && grouped))}</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  const selection = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <span style={SEC}>{selectedLabel} — {resolved.length ? `${resolved.length} ${noun}${resolved.length === 1 ? "" : "s"}` : "nothing yet"}</span>
      {(value ?? []).map((s, i) => {
        const isRule = s.type === "group";
        const g = isRule ? groups.find((x) => x.id === s.id) : null;
        const it = isRule ? null : items.find((x) => x.id === s.id);
        const n = isRule ? items.filter((x) => (x.group ?? "") === s.id).length : 0;
        const name = isRule ? (g?.name ?? "Missing department") : (it?.name ?? "No longer in the catalogue");
        const sub = isRule
          ? `Department · all ${n} ${noun}${n === 1 ? "" : "s"} — updates with your catalogue`
          : ((it?.meta ?? []).filter(Boolean).join(" · ") || (it ? "" : "Remove it or pick a replacement"));
        return (
          <div
            key={`${s.type}:${s.id}`}
            draggable={ordered}
            onDragStart={() => setDrag(i)}
            onDragEnd={() => { setDrag(null); setOver(null); }}
            onDragOver={(e) => { if (!ordered) return; e.preventDefault(); setOver(i); }}
            onDrop={() => { dropOn(i); setDrag(null); setOver(null); }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 9,
              border: `1px solid ${over === i ? "#5142C7" : it === null && !isRule ? "#E8B4B8" : "#E6E8EC"}`,
              background: isRule ? "#F6F2FE" : "#FFFFFF",
              cursor: ordered ? "grab" : "default", opacity: drag === i ? 0.5 : 1,
            }}>
            {ordered ? <span style={{ color: "#B5BAC6", fontSize: 12, flex: "none", letterSpacing: 1 }}>⋮⋮</span> : null}
            {g?.hue ? <span style={{ width: 9, height: 9, borderRadius: 999, background: g.hue, flex: "none" }} /> : null}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#333333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
              {sub ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{sub}</span> : null}
            </span>
            <span style={{ display: "inline-flex", gap: 3, flex: "none" }}>
              {ordered ? (
                <>
                  <button disabled={i === 0} onClick={() => move(i, -1)} style={{ border: "none", background: "transparent", color: i === 0 ? "#D1D4DC" : "#697180", cursor: i === 0 ? "default" : "pointer", fontSize: 11 }}>▲</button>
                  <button disabled={i === (value ?? []).length - 1} onClick={() => move(i, 1)} style={{ border: "none", background: "transparent", color: i === (value ?? []).length - 1 ? "#D1D4DC" : "#697180", cursor: i === (value ?? []).length - 1 ? "default" : "pointer", fontSize: 11 }}>▼</button>
                </>
              ) : null}
              <button onClick={() => removeAt(i)} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>✕</button>
            </span>
          </div>
        );
      })}
      {!(value ?? []).length ? (
        <div style={{ padding: "16px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center", lineHeight: 1.5 }}>
          {emptySelection ?? `Pick ${noun}s on the left — they appear here in the order the customer sees.`}
        </div>
      ) : null}
      {(value ?? []).some((s) => s.type === "group") ? (
        <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.5 }}>A department stays live: {noun}s added to it later are offered automatically. Publish shows what it resolves to today.</div>
      ) : null}
    </div>
  );

  if (dense) return <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>{source}{selection}</div>;
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, alignItems: "start" }}>{source}{selection}</div>;
}
